import { useEffect, useRef, useState } from "react";
import { GripVertical, Trash2, Check, Pencil, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { STICKY_COLORS, type StickyColor, type StickyNote } from "@/hooks/useStickyNotes";

export const STICKY_WIDTH = 224;
export const STICKY_HEIGHT = 176;

interface Props {
  note: StickyNote;
  readOnly?: boolean;
  onDragStart: (e: React.PointerEvent, note: StickyNote) => void;
  isDragging: boolean;
  onSave: (title: string, content: string) => void;
  onColor: (color: StickyColor) => void;
  onDelete: () => void;
}

export function StickyNoteCard({
  note,
  readOnly,
  onDragStart,
  isDragging,
  onSave,
  onColor,
  onDelete,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(note.title);
  const [content, setContent] = useState(note.content);
  const [showColors, setShowColors] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) {
      setTitle(note.title);
      setContent(note.content);
    }
  }, [note.title, note.content, editing]);

  useEffect(() => {
    if (editing) titleRef.current?.focus();
  }, [editing]);

  // Auto-save: debounce while typing, and flush on unmount (tab switch / navigation)
  const latest = useRef({ title, content, dirty: false });
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  useEffect(() => {
    const dirty = editing && (title !== note.title || content !== note.content);
    latest.current = { title, content, dirty };
    if (!dirty) return;
    const t = setTimeout(() => {
      onSaveRef.current(title, content);
      latest.current.dirty = false;
    }, 800);
    return () => clearTimeout(t);
  }, [title, content, editing, note.title, note.content]);
  useEffect(() => {
    const flush = () => {
      if (latest.current.dirty) {
        onSaveRef.current(latest.current.title, latest.current.content);
        latest.current.dirty = false;
      }
    };
    window.addEventListener("beforeunload", flush);
    return () => {
      window.removeEventListener("beforeunload", flush);
      flush();
    };
  }, []);

  const save = () => {
    latest.current.dirty = false;
    onSave(title, content);
    setEditing(false);
  };

  return (
    <div
      className={cn(
        "absolute rounded-md border p-2 flex flex-col gap-1 select-none",
        isDragging ? "opacity-90 cursor-grabbing z-20" : "z-10"
      )}
      style={{
        left: note.sticky_x,
        top: note.sticky_y,
        width: STICKY_WIDTH,
        minHeight: STICKY_HEIGHT,
        background: `hsl(var(--sticky-${note.sticky_color}))`,
        color: "hsl(var(--sticky-foreground))",
        borderColor: "hsl(var(--sticky-border))",
        boxShadow: "var(--shadow-sticky)",
      }}
    >
      <div className="flex items-center gap-1">
        {!readOnly && (
          <button
            type="button"
            aria-label="Move note"
            className="cursor-grab touch-none opacity-70 hover:opacity-100"
            onPointerDown={(e) => onDragStart(e, note)}
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        {editing ? (
          <Input
            ref={titleRef}
            value={title}
            maxLength={120}
            onChange={(e) => setTitle(e.target.value)}
            className="h-7 text-xs bg-transparent border-0 px-1 focus-visible:ring-0 font-semibold"
            placeholder="Title"
          />
        ) : (
          <p className="text-xs font-semibold truncate flex-1">{note.title || "Sticky note"}</p>
        )}
        {!readOnly && (
          <div className="flex items-center">
            {editing ? (
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={save} aria-label="Save note">
                <Check className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setEditing(true)}
                aria-label="Edit note"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setShowColors((v) => !v)}
              aria-label="Change colour"
            >
              <Palette className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onDelete} aria-label="Delete note">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>

      {showColors && !readOnly && (
        <div className="flex gap-1 pb-1">
          {STICKY_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Use ${c}`}
              className="h-4 w-4 rounded-full border"
              style={{ background: `hsl(var(--sticky-${c}))`, borderColor: "hsl(var(--sticky-border))" }}
              onClick={() => {
                onColor(c);
                setShowColors(false);
              }}
            />
          ))}
        </div>
      )}

      {editing ? (
        <Textarea
          value={content}
          maxLength={2000}
          onChange={(e) => setContent(e.target.value)}
          className="flex-1 text-xs bg-transparent border-0 px-1 resize-none focus-visible:ring-0 min-h-[96px]"
          placeholder="What should the team know?"
        />
      ) : (
        <p
          className="text-xs whitespace-pre-wrap flex-1 overflow-y-auto px-1 scrollable-content"
          onDoubleClick={() => !readOnly && setEditing(true)}
        >
          {note.content || (readOnly ? "" : "Double-click to write…")}
        </p>
      )}
    </div>
  );
}
